/**
 * Contract Code Mapping Admin
 * Manage mappings between client contract codes and internal client codes
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, Save, X, Loader2, Search, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import './ContractMappingAdmin.css';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';

const API = process.env.REACT_APP_BACKEND_URL;

const ContractMappingAdmin = ({ token }) => {
  const [mappings, setMappings] = useState([]);
  const [clientCodes, setClientCodes] = useState([]);
  const [productList, setProductList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const { searchInput: searchTerm, setSearchInput: setSearchTerm, debouncedValue: debouncedSearch } = useDebouncedSearch('', 250);
  const [isReady, setIsReady] = useState(false);
  const containerRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    contract_code: '',
    client_code_id: '',
    default_product: 'Tax Search',
    notes: ''
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [mappingsRes, clientCodesRes, productsRes] = await Promise.all([
        axios.get(`${API}/api/bulk-upload/contract-mappings?include_inactive=true`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API}/api/client-codes`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API}/api/products/orderable`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setMappings(mappingsRes.data.mappings || []);
      setClientCodes(clientCodesRes.data.client_codes || clientCodesRes.data || []);
      setProductList((productsRes.data.products || []).map(p => p.name));
    } catch (err) {
      setError('Failed to load data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Force reflow after data loads to fix visibility issue
  useEffect(() => {
    if (!loading && mappings.length >= 0) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (containerRef.current) {
          // Force browser reflow
          void containerRef.current.offsetHeight;
        }
        setIsReady(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [loading, mappings]);

  // Handle form change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Create mapping
  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      await axios.post(
        `${API}/api/bulk-upload/contract-mappings`,
        formData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setShowAddForm(false);
      setFormData({
        contract_code: '',
        client_code_id: '',
        default_product: 'Tax Search',
        notes: ''
      });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create mapping');
    } finally {
      setSaving(false);
    }
  };

  // Update mapping
  const handleUpdate = async (id) => {
    setSaving(true);
    setError(null);
    
    try {
      await axios.put(
        `${API}/api/bulk-upload/contract-mappings/${id}`,
        formData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      setEditingId(null);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update mapping');
    } finally {
      setSaving(false);
    }
  };

  // Delete mapping
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) return;
    
    try {
      await axios.delete(
        `${API}/api/bulk-upload/contract-mappings/${id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete mapping');
    }
  };

  // Start editing
  const startEdit = (mapping) => {
    setEditingId(mapping.id);
    setFormData({
      contract_code: mapping.contract_code,
      client_code_id: mapping.client_code_id,
      default_product: mapping.default_product || 'Tax Search',
      notes: mapping.notes || ''
    });
  };

  // Cancel edit
  const cancelEdit = () => {
    setEditingId(null);
    setFormData({
      contract_code: '',
      client_code_id: '',
      default_product: 'Tax Search',
      notes: ''
    });
  };

  // Filter mappings
  const filteredMappings = mappings.filter(m =>
    m.contract_code.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    m.client_code?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    m.client_name?.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="contract-mapping-loading">
        <Loader2 size={32} className="spin" />
        <p>Loading contract mappings...</p>
      </div>
    );
  }

  return (
    <div className="contract-mapping-admin" ref={containerRef} style={{ opacity: isReady ? 1 : 0.99 }}>
      <div className="mapping-header">
        <div className="header-left">
          <h2>Contract Code Mappings</h2>
          <p className="header-subtitle">
            Map client contract codes to internal client codes for bulk uploads
          </p>
        </div>
        <button 
          className="btn-add"
          onClick={() => setShowAddForm(true)}
          disabled={showAddForm}
        >
          <Plus size={18} />
          Add Mapping
        </button>
      </div>

      {error && (
        <div className="mapping-error">
          <AlertTriangle size={18} />
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Search */}
      <div className="mapping-search">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search by contract code, client code, or client name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="mapping-form-card">
          <h3>Add New Mapping</h3>
          <form onSubmit={handleCreate}>
            <div className="form-row">
              <div className="form-group">
                <label>Contract Code *</label>
                <input
                  type="text"
                  name="contract_code"
                  value={formData.contract_code}
                  onChange={handleChange}
                  placeholder="e.g., ABC-123"
                  required
                />
              </div>
              <div className="form-group">
                <label>Client Code *</label>
                <select
                  name="client_code_id"
                  value={formData.client_code_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a client code...</option>
                  {clientCodes.map(cc => (
                    <option key={cc.id} value={cc.id}>
                      {cc.code} - {cc.client_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Default Product</label>
                <select
                  name="default_product"
                  value={formData.default_product}
                  onChange={handleChange}
                >
                  {productList.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                  {!productList.includes(formData.default_product) && formData.default_product && (
                    <option value={formData.default_product}>{formData.default_product}</option>
                  )}
                </select>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <input
                  type="text"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => {
                setShowAddForm(false);
                setFormData({
                  contract_code: '',
                  client_code_id: '',
                  default_product: 'Tax Search',
                  notes: ''
                });
              }}>
                Cancel
              </button>
              <button type="submit" className="btn-save" disabled={saving}>
                {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
                Create Mapping
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Mappings Table */}
      <div className="mappings-table-container">
        <table className="mappings-table">
          <thead>
            <tr>
              <th>Contract Code</th>
              <th>Client Code</th>
              <th>Client Name</th>
              <th>Default Product</th>
              <th>Notes</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMappings.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-data">
                  {searchTerm ? 'No mappings found matching your search.' : 'No contract code mappings yet. Click "Add Mapping" to create one.'}
                </td>
              </tr>
            ) : (
              filteredMappings.map(mapping => (
                <tr key={mapping.id} className={!mapping.is_active ? 'inactive' : ''}>
                  {editingId === mapping.id ? (
                    <>
                      <td>
                        <input
                          type="text"
                          value={formData.contract_code}
                          onChange={handleChange}
                          name="contract_code"
                          className="edit-input"
                          disabled
                        />
                      </td>
                      <td>
                        <select
                          value={formData.client_code_id}
                          onChange={handleChange}
                          name="client_code_id"
                          className="edit-select"
                        >
                          {clientCodes.map(cc => (
                            <option key={cc.id} value={cc.id}>
                              {cc.code} - {cc.client_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>-</td>
                      <td>
                        <select
                          value={formData.default_product}
                          onChange={handleChange}
                          name="default_product"
                          className="edit-select"
                        >
                          {productList.map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                          {!productList.includes(formData.default_product) && formData.default_product && (
                            <option value={formData.default_product}>{formData.default_product}</option>
                          )}
                        </select>
                      </td>
                      <td>
                        <input
                          type="text"
                          value={formData.notes}
                          onChange={handleChange}
                          name="notes"
                          className="edit-input"
                        />
                      </td>
                      <td>-</td>
                      <td className="actions-cell">
                        <button 
                          className="btn-icon save"
                          onClick={() => handleUpdate(mapping.id)}
                          disabled={saving}
                        >
                          {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />}
                        </button>
                        <button className="btn-icon cancel" onClick={cancelEdit}>
                          <X size={16} />
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="contract-code">{mapping.contract_code}</td>
                      <td className="client-code">{mapping.client_code}</td>
                      <td>{mapping.client_name}</td>
                      <td>{mapping.default_product}</td>
                      <td className="notes-cell">{mapping.notes || '-'}</td>
                      <td>
                        <span className={`status-badge ${mapping.is_active ? 'active' : 'inactive'}`}>
                          {mapping.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button className="btn-icon edit" onClick={() => startEdit(mapping)}>
                          <Edit2 size={16} />
                        </button>
                        <button className="btn-icon delete" onClick={() => handleDelete(mapping.id)}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Help Section */}
      <div className="mapping-help">
        <h4>How Contract Code Mapping Works</h4>
        <ol>
          <li>When a CSV file is uploaded for bulk order creation, the system looks at the <strong>Contract Code</strong> column.</li>
          <li>It matches the contract code from the CSV against the mappings defined here.</li>
          <li>If a match is found, the order is automatically assigned to the corresponding <strong>Client Code</strong> with the <strong>Default Product</strong>.</li>
          <li>If no mapping is found, the row shows a validation error and cannot be imported.</li>
        </ol>
      </div>
    </div>
  );
};

export default ContractMappingAdmin;
